// ═══════════════════════════════════════════════════════════════════════════
// PERIODO LAVORATIVO E GIORNI ATTESI — regola unica (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Quali giorni una persona DEVE avere compilati nel cartellino. La usano il cartellino
// (Presenze), la nota spese (blocco invio) e la registrazione delle assenze, così i numeri
// coincidono sempre. Prima lo stesso conteggio era scritto due volte, in due modi.
//
// Si contano i giorni feriali (lunedì–venerdì, esclusi i festivi nazionali):
//   • solo dall'assunzione in poi e fino all'eventuale cessazione (anagrafica Dipendenti);
//     se la data di assunzione manca, dalla data di creazione dell'account;
//   • nel mese in corso solo fino a ieri (oggi è ancora in corso), nei mesi futuri nessuno;
//   • per chi NON è tenuto a timbrare nessun giorno è richiesto. La fonte è quella che
//     esiste già: Presenze → "Gestisci esclusioni timbratura" (documento
//     presenzeEsclusioni/{orgId}). Nessuna seconda impostazione parallela.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

export const pad2 = (n) => String(n).padStart(2, "0");
export const dataStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** "YYYY-MM-DD" → Date locale a mezzanotte (null se non valida). */
export function parseData(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d) ? null : d;
}

function calcolaPasqua(anno) {
  const a = anno % 19, b = Math.floor(anno / 100), c = anno % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31), giorno = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(anno, mese - 1, giorno);
}
const cacheFestivi = new Map();
/** Festività nazionali italiane dell'anno, come "YYYY-MM-DD". */
export function festivitaItaliane(anno) {
  if (cacheFestivi.has(anno)) return cacheFestivi.get(anno);
  const pasqua = calcolaPasqua(anno);
  const pasquetta = new Date(pasqua); pasquetta.setDate(pasqua.getDate() + 1);
  const set = new Set([
    `${anno}-01-01`, `${anno}-01-06`, dataStr(pasqua), dataStr(pasquetta),
    `${anno}-04-25`, `${anno}-05-01`, `${anno}-06-02`, `${anno}-08-15`,
    `${anno}-11-01`, `${anno}-12-08`, `${anno}-12-25`, `${anno}-12-26`,
  ]);
  cacheFestivi.set(anno, set);
  return set;
}

/** Giorno feriale: lunedì–venerdì e non festivo. */
export function eFeriale(d) {
  const dow = d.getDay();
  return dow !== 0 && dow !== 6 && !festivitaItaliane(d.getFullYear()).has(dataStr(d));
}

/**
 * Giorni (numeri 1..31) che la persona deve avere compilati nel mese.
 * @param {number} anno
 * @param {number} mese0  mese 0-based (0 = gennaio), come negli stati delle pagine
 * @param {object} periodo  { soggetto, inizio: Date|null, fine: Date|null }
 */
export function giorniAttesiMese(anno, mese0, periodo, oggi = new Date()) {
  if (periodo && periodo.soggetto === false) return [];
  const ieri = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() - 1);
  const ultimo = new Date(anno, mese0 + 1, 0).getDate();
  const out = [];
  for (let g = 1; g <= ultimo; g++) {
    const d = new Date(anno, mese0, g);
    if (d > ieri) break;                                   // oggi e futuro: non ancora richiesti
    if (periodo?.inizio && d < periodo.inizio) continue;   // prima dell'assunzione
    if (periodo?.fine && d > periodo.fine) continue;       // dopo la cessazione
    if (!eFeriale(d)) continue;
    out.push(g);
  }
  return out;
}

/** Giorni attesi senza nessuna timbratura (lavoro, ferie, permesso o malattia). */
export function giorniMancanti(attesi, giorniCartellino) {
  return attesi.filter(g => !((giorniCartellino?.[g]?.timbrature) || []).length);
}

/** Periodo lavorativo da scheda dipendente + account + esclusioni (funzione pura, testabile). */
export function componiPeriodo(dipendente, utente, escluso = false) {
  const inizioDip = parseData(dipendente?.dataAssunzione);
  const creato = utente?.createdAt?.toDate ? utente.createdAt.toDate() : null;
  const inizioAccount = creato ? new Date(creato.getFullYear(), creato.getMonth(), creato.getDate()) : null;
  const soggetto = !escluso;
  return {
    soggetto,
    inizio: inizioDip || inizioAccount,
    fine: parseData(dipendente?.dataCessazione),
    fonteInizio: inizioDip ? "assunzione" : (inizioAccount ? "account" : null),
    dipendenteId: dipendente?.id || null,
  };
}

/** Periodo lavorativo della persona uid nell'organizzazione orgId. */
export function usePeriodoLavorativo(orgId, uid) {
  const [stato, setStato] = useState({ pronto: false, soggetto: true, inizio: null, fine: null, fonteInizio: null, dipendenteId: null });
  useEffect(() => {
    if (!orgId || !uid) return;
    let annullato = false;
    (async () => {
      let dipendente = null, utente = null, escluso = false;
      try {
        const snap = await getDocs(query(collection(db, "dipendenti"),
          where("organizationId", "==", orgId), where("nexoraUid", "==", uid)));
        if (!snap.empty) dipendente = { id: snap.docs[0].id, ...snap.docs[0].data() };
      } catch (e) { console.warn("Periodo lavorativo: scheda dipendente non letta", e); }
      try {
        const u = await getDoc(doc(db, "users", uid));
        if (u.exists()) utente = u.data();
      } catch (e) { console.warn("Periodo lavorativo: account non letto", e); }
      try {
        const es = await getDoc(doc(db, "presenzeEsclusioni", orgId));
        escluso = es.exists() && (es.data().uids || []).includes(uid);
      } catch (e) { console.warn("Periodo lavorativo: esclusioni non lette", e); }
      if (!annullato) setStato({ pronto: true, ...componiPeriodo(dipendente, utente, escluso) });
    })();
    return () => { annullato = true; };
  }, [orgId, uid]);
  return stato;
}
